<script lang="ts" setup>
import { flattenEntity } from 'strapi-flatten-graphql'
import type { StrapiResp } from '~/models/Strapi'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const region = useCookie('region', { path: '/', domain: config.public.COOKIE_DOMAIN })
const isLoading = ref(false)
const articles = ref()
const author = ref()
const page = ref(route.query.page ? Number(route.query.page) : 1)
const size = 6
const total = ref()
const socmedIcon = [
  { type: 'facebook', icon: 'logos:facebook' },
  { type: 'instagram', icon: 'skill-icons:instagram' },
  { type: 'linkedin', icon: 'devicon:linkedin' },
  { type: 'twitter', icon: 'devicon:twitter' },
  { type: 'tiktok', icon: 'logos:tiktok-icon' },
]

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

getAuthor(useStrapiLocale(useGetSubdomain(), null))
getArticles(useStrapiLocale(useGetSubdomain(), null))

async function getAuthor(lang: string) {
  isLoading.value = true
  const { data, status, error } = await useStrapiAPI<StrapiResp>(`/authors?filters[username][$eq]=${route.params.slug}&populate=*&publicationState=live&locale=${lang}`, {
    key: 'get_author',
    initialCache: false,
  })
  const resp = data.value as { data: any }
  if (status.value === 'success' && !isEmpty(resp)) {
    const flattenResp = flattenEntity(resp.data[0])
    author.value = { ...flattenResp, thumbnail: !isEmpty(flattenResp.thumbnail) ? flattenResp.thumbnail?.data.map((item: any) => flattenEntity(item))[0] : [] }
    setMeta(flattenResp?.meta, author.value.name, author.value.position, author.value.desc)
  }
  else {
    showError({ message: 'Page not found', statusCode: 404 })
  }
}

async function getArticles(lang: string) {
  const { data, status, error } = await useStrapiAPI<StrapiResp>(`/articles/?filters[createdBy][username][$eq]=${route.params.slug}&sort=createdAt:desc&pagination[page]=${page.value}&pagination[pageSize]=${size}&populate[meta]=*&populate[createdBy]=*&populate[thumbnail]=*&populate[file_attachment]=*&populate[topics]=*&populate[contents][populate][image]=*&populate[contents][populate][items]=*&publicationState=live&locale=${lang}`, {
    key: 'get_article',
    initialCache: false,
  })
  if (status.value === 'success' && !isEmpty(data.value)) {
    // flatten the data
    total.value = data.value.meta?.pagination?.total
    articles.value = data.value.data.map((item: any) => {
      const article = flattenEntity(item)
      const thumbnail = article.thumbnail?.data ? flattenEntity(article.thumbnail?.data) : null
      const topics = article.topics?.data.map((topic: any) => flattenEntity(topic))
      const createdBy = article.createdBy?.data ? flattenEntity(article.createdBy?.data) : null
      const fileAttachment = article.file_attachment?.data ? flattenEntity(article.file_attachment?.data) : null
      return { ...article, thumbnail, topics, createdBy, file_attachment: fileAttachment }
    })
  }
  else {
    showError({ message: 'Page not found', statusCode: 404 })
  }

  isLoading.value = false
}

function setFilter() {
  router.push({ path: route.path, query: { page: page.value } })
  getArticles(useStrapiLocale(useGetSubdomain(), null), author.value.username)
}

function setMeta(metaData: any, name: string, position: string, desc: string) {
  const title = metaData?.title || `${name} - ${position} | Epicareer` || ''
  const description = metaData?.description || desc || ''
  const keywords = metaData?.keywords || ''
  const robots = (config.public.ENV === 'development' || config.public.ENV === 'staging') ? 'nofollow, noindex' : (metaData?.robots || 'follow, index')
  const canonical = metaData?.canonical || useSetSubdomain(region.value) + route.path || ''
  const seoImage = author.value.thumbnail?.url || 'https://files.epicareer.com/assets/images/share.png'

  useHead({
    title,
    meta: [
      { property: 'og:title', content: title },
      { name: 'twitter:title', content: title },
      { name: 'description', content: description },
      { property: 'og:description', content: description },
      { name: 'twitter:description', content: description },
      { name: 'keywords', content: keywords || null },
      { property: 'og:url', content: route.fullPath },
      { name: 'twitter:url', content: route.fullPath },
      { property: 'og:type', content: 'article' },
      { property: 'og:image:type', content: 'image/png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { property: 'og:image', content: seoImage },
      { name: 'twitter:image', content: seoImage },
      { property: 'og:image:secure_url', content: seoImage },
      { name: 'twitter:image:src', content: seoImage },
      { name: 'robots', content: robots },
    ],
    link: [
      { rel: 'canonical', href: canonical },
    ],
  })
}
</script>

<template>
  <BaseAtomsSkeletonBounded v-if="!isLoading" container="full" class="max-w-6xl !xl:px-0 !lg:pt-10 mx-auto text-gray-900">
    <div class="<lg:px-4 <lg:pt-6">
      <Icon name="heroicons:arrow-left-solid" class="h-6 w-6 cursor-pointer mb-4 md:mb-6" @click="$router.back()" />
    </div>
    <div class="flex-col-reverse flex lg:grid grid-cols-12 gap-x-6 auto-rows-min px-4 lg:px-0">
      <div class="w-full grid gap-y-4 lg:col-spans-7">
        <div v-if="isLoading" class="grid gap-y-4">
          <CareerGuideArticleCard v-for="(i) in 4" :key="i" :compact-mode="false" :is-loading="true" />
        </div>
        <div v-else-if="!isLoading && !isEmpty(articles)" class="grid gap-y-4">
          <CareerGuideArticleCard v-for="(item, idx) in articles" :key="idx" :compact-mode="false" :is-loading="false" :article="item" :author="author" />
          <div class="bg-white px-4 py-3 text-xs">
            <div class="mb-3 text-right text-gray-600">
              Showing {{ page === 1 ? 1 : ((page - 1) * size + 1) }}-{{ page === 1 ? articles.length : ((page - 1) * size) + articles.length }} of {{ total }} items
            </div>
            <div class="flex">
              <vue-awesome-paginate
                v-model="page"
                :total-items="total"
                :items-per-page="size"
                :max-pages-shown="3"
                :hide-prev-next="true"
                type="link"
                :link-url="`${route.path}?page=[page]`"
                pagination-container-class="flex"
                paginate-buttons-class="w-8.5 h-9 leading-8 text-center border border-gray-300 bg-white rounded-sm cursor-pointer ml-2 rounded-md truncate !block px-1"
                active-page-class="!bg-primary-500 !border-primary-500 text-white font-medium"
                class="ml-auto"
                @click="setFilter()"
              />
            </div>
          </div>
        </div>
        <div v-else-if="!isLoading && isEmpty(articles)" class="text-center">
          <img class="w-64 h-64 mx-auto" src="/images/career-guide/empty-state.png">
          <p class="text-xl font-bold mb-2">
            Author has no article yet...
          </p>
          <p class="text-sm">
            Articles will shown here
          </p>
        </div>
      </div>
      <div class="w-full grid gap-y-4 lg:col-spans-5 auto-rows-min <lg:mb-4">
        <div class="bg-white p-6 pb-8 rounded-lg grid gap-y-6">
          <div class="flex items-center">
            <div class="flex-shrink-0 flex items-center mr-4">
              <img :src="author?.thumbnail?.url" class="w-20 h-20 object-cover rounded-full">
            </div>
            <div>
              <p class="text-2xl font-bold">
                {{ author.name }}
              </p>
              <p class="text-gray-600">
                {{ author.position }}
              </p>
            </div>
          </div>
          <div>
            {{ author.description }}
          </div>
          <div class="flex items-center space-x-6">
            <a v-for="(item, idx) in author.social_media" :key="idx" target="_blank" :href="item.url">
              <Icon :name="socmedIcon.find(icon => icon.type === item.type)?.icon" class="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </BaseAtomsSkeletonBounded>
</template>
